"use client";

import { cn } from "@repo/design-system/lib/utils";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Button } from "@/app/components/CustomButton";
import { env } from "@/env";

export default function Header() {
	const [isScrolled, setIsScrolled] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 10);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Mobile menü açıkken scroll'u engelle
	useEffect(() => {
		if (isMobileMenuOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isMobileMenuOpen]);

	// NEXT_PUBLIC_LANDING_PAGE_URL optional olduğu için tanımsız kaldığında
	// template string "undefined/case" üretip relative URL'e düşüyor.
	const landingPageUrl = (
		env.NEXT_PUBLIC_LANDING_PAGE_URL ?? "https://projectquadrant.com"
	).replace(/\/$/, "");

	const menu = [
		{ name: "Product", link: `${landingPageUrl}/#product-features` },
		{ name: "Solution", link: `${landingPageUrl}/solutions` },
		{ name: "Pricing", link: `${landingPageUrl}/pricing` },
		{ name: "Case", link: `${landingPageUrl}/case` },
	];

	const signInOnclick = () => {
		window.location.href = "/sign-in";
	};

	const startTrialOnclick = () => {
		window.location.href = "/sign-up";
	};

	const logoOnclick = () => {
		const isHomePage = window.location.pathname === "/";
		const hasHash = window.location.hash;

		if (isHomePage || hasHash) {
			window.scrollTo({ top: 0, behavior: "smooth" });
		} else {
			window.location.href = "/";
		}
	};

	return (
		<header
			className={`sticky top-0 z-50 border-0 md:mx-32 ${isScrolled ? "md:top-0.5" : "bg-[#F5F5F0]"}`}
		>
			<div
				className={cn(
					"container mx-auto border-0 md:border-[#E6E1D2] transition-all duration-300 flex items-center justify-between py-2 px-4 ",
					isScrolled
						? "md:max-w-[60%] bg-[#F5F5F0]/80 md:border md:px-2! box md:m-auto md:rounded-full backdrop-blur-lg shadow-lg"
						: "md:px-0",
				)}
			>
				<Image
					src="/logos/logo.svg"
					alt="Logo"
					className="hover:cursor-pointer"
					width={120}
					height={40}
					onClick={logoOnclick}
				/>

				{/* Desktop Navigation */}
				<nav className="hidden md:block">
					<ul className="flex space-x-4">
						{menu.map((item) => (
							<li key={item.name}>
								<a
									className="font-bold text-sm text-[#1A2314]"
									href={item.link}
								>
									{item.name}
								</a>
							</li>
						))}
					</ul>
				</nav>

				{/* Desktop Buttons */}
				<div className="hidden md:flex items-center">
					<Button
						variant="ghost"
						className="text-[#6F6F6F] text-sm font-bold hover:bg-[#1A2314]/5 hover:text-[#1A2314]"
						onClick={signInOnclick}
					>
						Log in
					</Button>
					<Button
						variant="outline"
						className="text-[#004A7C] bg-transparent ring-0 border-2 border-[#004A7C] text-sm rounded-full h-12 font-bold py-3 px-6 transition-all duration-500 hover:bg-[#004A7C] hover:text-white"
						onClick={startTrialOnclick}
					>
						Start Trial
					</Button>
				</div>

				{/* Mobile Hamburger Button */}
				<button
					type="button"
					className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5"
					onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
					aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
					aria-expanded={isMobileMenuOpen}
				>
					<span
						className={`block w-6 h-0.5 bg-[#1A2314] transition-all duration-300 ${
							isMobileMenuOpen ? "rotate-45 translate-y-2" : ""
						}`}
					/>
					<span
						className={`block w-6 h-0.5 bg-[#1A2314] transition-all duration-300 ${
							isMobileMenuOpen ? "opacity-0" : ""
						}`}
					/>
					<span
						className={`block w-6 h-0.5 bg-[#1A2314] transition-all duration-300 ${
							isMobileMenuOpen ? "-rotate-45 -translate-y-2" : ""
						}`}
					/>
				</button>
			</div>

			{/* Mobile Menu Overlay */}
			<div
				className={`fixed inset-0 top-14 bg-[#F5F5F0] z-40 md:hidden transition-all duration-300 ${
					isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
				}`}
			>
				<div className="flex flex-col h-full px-6 py-8">
					{/* Mobile Navigation */}
					<nav className="flex-1">
						<ul className="flex flex-col gap-6">
							{menu.map((item) => (
								<li key={item.name}>
									<a
										className="text-2xl font-bold block text-[#1A2314]"
										href={item.link}
										onClick={() => setIsMobileMenuOpen(false)}
									>
										{item.name}
									</a>
								</li>
							))}
						</ul>
					</nav>

					{/* Mobile Buttons */}
					<div className="flex flex-col gap-3 pb-8">
						<Button
							variant="ghost"
							className="text-[#1A2314] text-lg font-bold w-full justify-center py-3 hover:bg-[#1A2314]/5 hover:text-[#1A2314]"
							onClick={signInOnclick}
						>
							Log in
						</Button>
						<Button
							variant="outline"
							className="text-[#004A7C] bg-transparent ring-0 border-2 border-[#004A7C] rounded-full font-bold py-4 px-6 transition-all duration-500 hover:bg-[#004A7C] hover:text-white w-full justify-center"
							onClick={startTrialOnclick}
						>
							Start Trial
						</Button>
					</div>
				</div>
			</div>
		</header>
	);
}
